import { NavLink, useParams } from 'react-router-dom'
import {
  LayoutDashboard,
  ListTree,
  Layers,
  Kanban,
  Calendar,
  GanttChart,
  CalendarRange,
  Users,
  Settings,
  Inbox,
  Plus,
  TrendingUp,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { lookupProjects } from '../api/projects'
import { Avatar, cn } from '../design'

const SECTIONS: Array<{
  label: string
  items: Array<{ to: string; label: string; icon: React.ComponentType<{ size?: number }> }>
}> = [
  {
    label: 'Personal',
    items: [
      { to: '/my-work',  label: 'My Work',  icon: Inbox },
      { to: '/calendar', label: 'Calendar', icon: Calendar },
    ],
  },
]

const PROJECT_ITEMS: Array<{ to: string; label: string; icon: React.ComponentType<{ size?: number }> }> = [
  { to: '',           label: 'Dashboard', icon: LayoutDashboard },
  { to: 'backlog',    label: 'Backlog',   icon: ListTree },
  { to: 'epics',      label: 'Epics',     icon: Layers },
  { to: 'board',      label: 'Board',     icon: Kanban },
  { to: 'sprints',    label: 'Sprints',   icon: CalendarRange },
  { to: 'calendar',   label: 'Calendar',  icon: Calendar },
  { to: 'timeline',   label: 'Timeline',  icon: GanttChart },
  { to: 'team',       label: 'Team',      icon: Users },
  { to: 'reports',    label: 'Reports',   icon: TrendingUp },
  { to: 'settings',   label: 'Settings',  icon: Settings },
]

export function Sidebar() {
  const { projectKey } = useParams<{ projectKey?: string }>()
  const projectsQ = useQuery({ queryKey: ['lookups', 'projects'], queryFn: lookupProjects, staleTime: 60_000 })
  const projects = projectsQ.data ?? []

  return (
    <aside className="flex h-full w-60 flex-col border-r border-border bg-bg-elevated">
      <div className="px-4 h-14 flex items-center gap-2 border-b border-border">
        <div className="h-7 w-7 rounded-md bg-accent text-accent-fg flex items-center justify-center text-sm font-bold">A</div>
        <div className="leading-tight">
          <div className="text-sm font-semibold tracking-tight">Atlas</div>
          <div className="text-[10px] text-fg-subtle">DireTek workspace</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-5">
        {SECTIONS.map(section => (
          <div key={section.label}>
            <SectionLabel>{section.label}</SectionLabel>
            <ul className="space-y-0.5 mt-1">
              {section.items.map(item => (
                <li key={item.to}>
                  <NavLink to={item.to} className={navLinkClass}>
                    <item.icon size={15} />
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div>
          <div className="flex items-center justify-between pl-2 pr-1">
            <SectionLabel>Projects</SectionLabel>
            <NavLink
              to="/projects/new"
              title="New project"
              className="text-fg-subtle hover:text-fg p-1 rounded hover:bg-bg-muted"
            >
              <Plus size={13} />
            </NavLink>
          </div>
          <ul className="space-y-0.5 mt-1">
            {projectsQ.isLoading && <li className="px-2 py-1 text-xs text-fg-subtle">Loading…</li>}
            {!projectsQ.isLoading && projects.length === 0 && (
              <li className="px-2 py-1 text-xs text-fg-subtle">No projects yet</li>
            )}
            {projects.map(p => (
              <li key={p.id}>
                <NavLink
                  to={`/projects/${p.key}`}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm',
                      'text-fg-muted hover:text-fg hover:bg-bg-muted',
                      (isActive || p.key === projectKey) && 'bg-accent-soft text-accent-hover',
                    )
                  }
                >
                  <Avatar size="xs" name={p.name} color={p.color ?? '#9aa0a6'} />
                  <span className="font-medium truncate">{p.name}</span>
                  <span className="ml-auto font-mono text-[10px] text-fg-subtle">{p.key}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </div>

        {projectKey && (
          <div className="border-t border-border pt-3">
            <SectionLabel>{projectKey}</SectionLabel>
            <ul className="space-y-0.5 mt-1">
              {PROJECT_ITEMS.map(item => (
                <li key={item.to}>
                  <NavLink
                    end={item.to === ''}
                    to={item.to === 'calendar'
                      // The calendar is personal; this opens it filtered to the project.
                      ? `/calendar?project=${projectKey}`
                      : `/projects/${projectKey}${item.to ? '/' + item.to : ''}`}
                    className={navLinkClass}
                  >
                    <item.icon size={15} />
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        )}
      </nav>

      <div className="px-3 py-3 text-[10px] text-fg-subtle border-t border-border">
        v0.1 · P3 design system
      </div>
    </aside>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-2 text-[10px] font-semibold uppercase tracking-wider text-fg-subtle">{children}</div>
  )
}

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return cn(
    'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm',
    'text-fg-muted hover:text-fg hover:bg-bg-muted',
    isActive && 'bg-accent-soft text-accent-hover font-medium',
  )
}
